// This version delegates the definition of “yesterday” and the last-N-days window
// to src/common/utils/dashboardDateKeys.ts so the dashboard and charts stay aligned.

import {getDashboardLastNDaysEndingYesterdayKeys} from '../../../common/utils/dashboardDateKeys'

export type IdLike = {id: string}

export type RentalLite = {
  rentStart?: string | number | Date
  createdAt?: string | number | Date // fallback
  startDate?: string | number | Date // fallback
  rentedAt?: string | number | Date // fallback
  rentBy: IdLike
  product: IdLike & {[k: string]: any}
  box: IdLike & {[k: string]: any}
}

export type EntityLite = IdLike

export interface ComputeDashboardMetricsInput<T extends RentalLite> {
  rentals: T[]

  // Optional: if provided, totals will prefer these lengths over deriving from rentals
  users?: EntityLite[]
  products?: EntityLite[]
  boxes?: EntityLite[]

  now?: Date // default: new Date(); truncated to local midnight internally
  daysBack?: number // default: 14
  preferredDateFields?: string[] // default: ['rentStart','createdAt','startDate','rentedAt']
}

export interface DashboardMetrics<T extends RentalLite> {
  // Exact list clamped to [fromKey .. end-of-window] inclusive (local days)
  windowedRentals: T[]

  total: {
    rentals: number
    users: number
    products: number
    boxes: number
  }

  lastNDays: {
    rentals: number
    usersWithRentals: number
    usedProducts: number
    usedBoxes: number
    productUsage: {[productId: string]: {product: any; count: number}}
    boxUsage: {[boxId: string]: {box: any; count: number}}
  }

  yesterday: {
    rentals: number
    usersWithRentals: number
    usedProducts: number
    usedBoxes: number
  }

  _meta: {
    todayKey: string // local YYYY-MM-DD
    yesterdayKey: string // local YYYY-MM-DD
    fromKey: string // start of the lastNDays window (local day)
  }
}

/* ---------------- Local date helpers (midnight→midnight, local) ---------------- */

function toLocalDayKeyFromDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseDateLikeLocal(value: unknown): Date | null {
  if (value == null) return null

  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (m) {
      const [, ys, ms, ds] = m
      return new Date(Number(ys), Number(ms) - 1, Number(ds), 0, 0, 0, 0)
    }
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }

  if (typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }

  return null
}

function toLocalDayKey(value: unknown): string | null {
  const d = parseDateLikeLocal(value)
  return d ? toLocalDayKeyFromDate(d) : null
}

function addDaysLocal(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0)
  d.setDate(d.getDate() + days)
  return d
}

function firstAvailableDateKey(rental: RentalLite, preferred: string[]): string | null {
  for (let i = 0; i < preferred.length; i++) {
    const field = preferred[i] as keyof RentalLite
    const key = toLocalDayKey((rental as any)[field])
    if (key) return key
  }
  return null
}

function inClosedRange(dayKey: string, fromKey: string, toKey: string): boolean {
  // Lexicographic compare is valid for YYYY-MM-DD
  return dayKey >= fromKey && dayKey <= toKey
}

/* ---------------- Public API ---------------- */

export function computeDashboardMetrics<T extends RentalLite>(input: ComputeDashboardMetricsInput<T>): DashboardMetrics<T> {
  const {
    rentals,
    users,
    products,
    boxes,
    now = new Date(),
    daysBack = 14,
    preferredDateFields = ['rentStart', 'createdAt', 'startDate', 'rentedAt'],
  } = input

  // Use the shared, canonical window: last N days *ending yesterday*.
  const {startKey: fromKey, endKey: yesterdayKey} = getDashboardLastNDaysEndingYesterdayKeys(daysBack, now)

  // Today is the day after "yesterday" (local). Compute from day key to avoid TZ/UTC quirks.
  const yParts = yesterdayKey.split('-').map((s) => parseInt(s, 10)) // [yyyy, mm, dd]
  const yDate = new Date(yParts[0], yParts[1] - 1, yParts[2], 0, 0, 0, 0)
  const todayKey = toLocalDayKeyFromDate(addDaysLocal(yDate, 1))

  // Clamp rentals to the canonical window [fromKey .. yesterdayKey]
  const windowed: T[] = []
  for (let i = 0; i < rentals.length; i++) {
    const r = rentals[i]
    const k = firstAvailableDateKey(r, preferredDateFields)
    if (k && inClosedRange(k, fromKey, yesterdayKey)) {
      windowed.push(r)
    }
  }

  // Totals: prefer provided arrays; otherwise derive from *all provided rentals* (not just windowed)
  const derivedUserIds = new Set<string>()
  const derivedProductIds = new Set<string>()
  const derivedBoxIds = new Set<string>()
  if (!users || !products || !boxes) {
    for (let i = 0; i < rentals.length; i++) {
      const r = rentals[i] as RentalLite
      const u = r?.rentBy?.id
      const p = r?.product?.id
      const b = r?.box?.id
      if (!users && u) derivedUserIds.add(u)
      if (!products && p) derivedProductIds.add(p)
      if (!boxes && b) derivedBoxIds.add(b)
    }
  }

  // Last N days aggregations (over windowed)
  let lastNDaysRentals = 0
  const lastUsers = new Set<string>()
  const lastProducts = new Set<string>()
  const lastBoxes = new Set<string>()
  const productUsage: {[pid: string]: {product: any; count: number}} = {}
  const boxUsage: {[bid: string]: {box: any; count: number}} = {}

  // Yesterday aggregations
  let yRentals = 0
  const yUsers = new Set<string>()
  const yProducts = new Set<string>()
  const yBoxes = new Set<string>()

  for (let i = 0; i < windowed.length; i++) {
    const r = windowed[i] as RentalLite
    const u = r?.rentBy?.id
    const p = r?.product?.id
    const b = r?.box?.id

    // Last N days
    lastNDaysRentals++
    if (u) lastUsers.add(u)
    if (p) {
      lastProducts.add(p)
      const entry = productUsage[p] || {
        product: (r as any).product,
        count: 0,
      }
      entry.count += 1
      productUsage[p] = entry
    }
    if (b) {
      lastBoxes.add(b)
      const entry = boxUsage[b] || {box: (r as any).box, count: 0}
      entry.count += 1
      boxUsage[b] = entry
    }

    // Yesterday (exact match on local day key)
    const k = firstAvailableDateKey(r, preferredDateFields)
    if (k === yesterdayKey) {
      yRentals++
      if (u) yUsers.add(u)
      if (p) yProducts.add(p)
      if (b) yBoxes.add(b)
    }
  }

  return {
    windowedRentals: windowed,
    total: {
      rentals: rentals.length,
      users: users ? users.length : derivedUserIds.size,
      products: products ? products.length : derivedProductIds.size,
      boxes: boxes ? boxes.length : derivedBoxIds.size,
    },
    lastNDays: {
      rentals: lastNDaysRentals,
      usersWithRentals: lastUsers.size,
      usedProducts: lastProducts.size,
      usedBoxes: lastBoxes.size,
      productUsage,
      boxUsage,
    },
    yesterday: {
      rentals: yRentals,
      usersWithRentals: yUsers.size,
      usedProducts: yProducts.size,
      usedBoxes: yBoxes.size,
    },
    _meta: {todayKey, yesterdayKey, fromKey},
  }
}
